import { LabelText, TitleText } from '@policygenius/mortar';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import { removeGlobalError } from 'containers/App/actions';

import styles from './global-error.module.scss';
import { IProps } from './types';

const ONETRUST_BOTTOM_ELEMENT_SELECTORS = ['#onetrust-banner-sdk', '#ot-sdk-btn-floating'];
const ONETRUST_CLEARANCE_PX = 16;

const getVisibleOneTrustBottomOffset = () => {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return ONETRUST_BOTTOM_ELEMENT_SELECTORS.reduce((offset, selector) => {
    const element = document.querySelector<HTMLElement>(selector);

    if (!element) {
      return offset;
    }

    const computedStyles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const isVisible =
      computedStyles.display !== 'none' &&
      computedStyles.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0;

    if (!isVisible || rect.top >= viewportHeight) {
      return offset;
    }

    return Math.max(offset, Math.ceil(viewportHeight - rect.top + ONETRUST_CLEARANCE_PX));
  }, 0);
};

const useOneTrustBottomOffset = (enabled: boolean) => {
  const [bottomOffset, setBottomOffset] = useState(0);

  const updateBottomOffset = useCallback(() => {
    setBottomOffset(getVisibleOneTrustBottomOffset());
  }, []);

  useLayoutEffect(() => {
    if (!enabled) {
      setBottomOffset(0);

      return undefined;
    }

    updateBottomOffset();

    const mutationObserver = new MutationObserver(updateBottomOffset);

    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateBottomOffset) : undefined;

    for (const selector of ONETRUST_BOTTOM_ELEMENT_SELECTORS) {
      const element = document.querySelector(selector);

      if (element) {
        resizeObserver?.observe(element);
      }
    }

    window.addEventListener('resize', updateBottomOffset);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateBottomOffset);
    };
  }, [enabled, updateBottomOffset]);

  return bottomOffset;
};

const GlobalError: React.FC<IProps> = ({ error }) => {
  const dispatch = useDispatch();
  const handleClick = () => dispatch(removeGlobalError());
  const oneTrustBottomOffset = useOneTrustBottomOffset(Boolean(error));

  if (!error) {
    return null;
  }

  return (
    <div
      id="messages"
      className={styles.wrapper}
      style={
        {
          '--global-error-bottom-offset': `${oneTrustBottomOffset}px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.alert}>
        <div className={styles['alert-box-content']}>
          <LabelText color="white" size="large">
            {error}
          </LabelText>
          <Button
            analyticsProperties={{
              text: error,
              source: 'global-error',
            }}
            className={styles.close}
            onClick={handleClick}
            fullWidth={false}
            variant="secondary-no-fill"
          >
            <TitleText as="span" color="white" size="large">
              ×
            </TitleText>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GlobalError;
